import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Alert
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from '../../utils/axios';
import { recaptchaSiteKey } from '../../config';

const ResendVerification = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const recaptchaRef = useRef(null);
    const [email, setEmail] = useState('');
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const emailParam = searchParams.get('email');
        if (emailParam) {
            setEmail(emailParam);
        }
    }, [location.search]);

    const handleChange = (e) => {
        setEmail(e.target.value);
    };

    const resetRecaptcha = () => {
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        if (!email) {
            setError('Email is required');
            setLoading(false);
            return;
        }

        if (!recaptchaToken) {
            setError('Please complete the reCAPTCHA verification');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post('/api/users/resend-verification', {
                email,
                recaptchaToken
            });
            setSuccess(response.data.message || 'Verification email has been sent. Please check your inbox.');
            resetRecaptcha();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to resend verification email');
            resetRecaptcha();
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = () => {
        navigate('/login');
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Resend Verification Email
                </Typography>

                <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                    Enter your email address below, and we'll send you a new verification email.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={email}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />
                    {recaptchaSiteKey ? (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={recaptchaSiteKey}
                                onChange={(token) => setRecaptchaToken(token)}
                                onExpired={resetRecaptcha}
                            />
                        </Box>
                    ) : (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            reCAPTCHA is not configured. Set REACT_APP_RECAPTCHA_SITE_KEY.
                        </Alert>
                    )}
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={loading || !recaptchaToken}
                        >
                            {loading ? 'Sending...' : 'Resend Verification Email'}
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleLogin}
                            size="large"
                        >
                            Back to Login
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Container>
    );
};

export default ResendVerification;
